import React from "react";
import "./CardFlowRows.css";
import { formatCardNumber, ABSENT_VALUE } from "./summaryCardReadiness";
import {
  hasMemoRow,
  MEMO_CAPTION,
  MEMO_FOOTNOTE,
  TOTAL_CAPTION,
} from "./cardFlowRows";

/* The one renderer for a flow-grouped card body.
 *
 * Five surfaces show these rows -- the Power Flow diamond, the Charts tab's
 * flat row, and the Storage / Production / Consumption per-device cards. They
 * must agree on what a row means, on how an absent figure looks, and on where
 * the columns fall. One component, one column definition, no per-surface
 * variation to drift.
 *
 * ── Why this is a real <table> ────────────────────────────────────────────
 * It used to be nested CSS grids with ARIA roles layered on: each row its own
 * grid, and alignment holding only because every track was a fraction. That
 * worked, but it was a convention. Nothing structural stopped a row -- or a
 * card -- from resolving different columns, and in fact one did: when the label
 * track was content-sized it came out 0 / 42 / 41 / 49 / 86px down a single
 * card, and the column headings ended up over the wrong columns.
 *
 * A table makes it structural. `table-layout: fixed` plus the single
 * <colgroup> below means the browser computes one set of column widths and
 * every row in the table is bound to it -- there is no per-row template left to
 * disagree with. The colgroup is a constant in this file, so every card on
 * every surface is laid out from the same definition.
 *
 * It also replaces the hand-rolled ARIA with native semantics: <th scope="col">
 * is a columnheader, <th scope="row"> a rowheader, <td> a cell. Headings can no
 * longer be returned by anything reading value cells, because they are not
 * cells. That property used to rest on a class name.
 *
 * The earlier reasoning for avoiding a table -- that the grid was measured and
 * known to fit -- did not survive having to fix the grid to keep it aligned.
 * The fixed layout is re-measured; see the notes in CardFlowRows.css.
 */

/* The three value columns, and what they actually contain.
 *
 * Worth spelling out on the card: the kW figures are maxima over the selected
 * period while the kWh figures are sums over it. Nothing on the card said so,
 * and a reader could reasonably have taken both for totals, or both for
 * instantaneous readings. */
/* The unit lives here, once per column, instead of being repeated on every
 * figure down the card. The cells below are bare numbers.
 *
 * Money is signalled by "($)" in the heading rather than a "$" on each figure,
 * for the same reason: one statement per column. Negatives keep their leading
 * minus, so "-546,321" under a "VALUE ($)" heading still reads as a cost --
 * checked in the browser at every width, not assumed.
 *
 * The unit is also repeated in each cell's title tooltip, and screen readers
 * get it for free: these are real <th scope="col"> headers, so a cell is
 * announced with its column heading. That is the answer to the heading
 * scrolling out of view -- see the note on scrolling in CardFlowRows.css. */
/* ── Two lines per heading, one heading ────────────────────────────────────
 *
 *      POWER          ENERGY          VALUE
 *     Max (kW)      Total (kWh)         ($)
 *
 * `quantity` is what the column HOLDS; `qualifier` is what the arithmetic did to
 * it, with the unit. The quantity leads because that is what a reader scans
 * for, and because the three quantities are what distinguish the columns --
 * "Max" and "Total" describe the operation, which is the secondary fact. It
 * also matters here specifically: a single row carries a power, an energy and a
 * money figure, which are easy to conflate at a glance.
 *
 * The two lines are one <th scope="col">, not two header rows and not two
 * cells. That is what makes a screen reader announce the whole heading with
 * every value cell, which is how the unit stays available to a listener and to
 * anyone who has scrolled the heading out of view. Splitting it would break
 * that association -- which is the entire reason the units live up here rather
 * than being repeated on every figure.
 *
 * Value's second line is the bare unit, with no qualifier. There is no honest
 * one available: the money column is a NET, so "Total" would misdescribe it,
 * and it would also contradict the Energy column's "Total" sitting beside it.
 * The unit alone keeps all three second lines aligned on the thing they have in
 * common. It is deliberately NOT filled with an em-dash placeholder: on these
 * cards the em-dash means "not recorded", and putting one in a heading would
 * collide with the one glyph this card cannot afford to make ambiguous. */
const COLUMN_HEADINGS = [
  {
    key: "power",
    quantity: "Power",
    qualifier: "Max",
    unit: "kW",
    title: "Highest value reached in the period, in kW",
  },
  {
    key: "energy",
    quantity: "Energy",
    qualifier: "Total",
    unit: "kWh",
    title: "Summed over the period, in kWh",
  },
  {
    key: "cost",
    quantity: "Value",
    qualifier: null,
    unit: "$",
    title: "Money in dollars, as the engine recorded it",
  },
];

/* Unit by column, for the per-cell tooltips. */
const UNIT_BY_KIND = Object.fromEntries(
  COLUMN_HEADINGS.map(({ key, unit }) => [key, unit]),
);

/* Why a value cell is empty. The two blanks are different things and must not
 * be confused:
 *
 *   absent          the figure belongs on this row but nothing supplied it --
 *                   an em-dash, with a title saying why, never a $0
 *   not-applicable  the row has no such quantity at all. A demand charge has no
 *                   power and no energy to be missing, so those cells are
 *                   structurally blank. An em-dash there would claim a figure
 *                   went astray.
 *
 * Both are exposed as `data-cell-state` so a test can tell them apart, and the
 * non-present ones carry an aria-label so a screen reader hears the difference
 * rather than a bare empty cell or the words "em dash". */
const CELL_STATE = {
  present: null,
  absent: "not recorded",
  "not-applicable": "not applicable",
};

function ValueCell({ kind, state, text, title, testId }) {
  /* With the unit out of the cell, the tooltip carries it -- so a figure stays
   * self-describing even if the heading is scrolled out of view. */
  const unit = UNIT_BY_KIND[kind];
  const tooltip =
    state === "present" && unit ? [title, unit].filter(Boolean).join(" — ") : title;

  return (
    <td
      className={`card-flow__cell card-flow__cell--${kind}`}
      title={tooltip}
      data-cell-state={state}
      aria-label={CELL_STATE[state] || undefined}
      data-testid={testId}
    >
      {text}
    </td>
  );
}

/* A value cell's text and state. Bare numbers -- the unit is in the heading.
 * No `slot` at all means the row has no such quantity. */
function statCell(slot) {
  if (!slot) return { state: "not-applicable", text: "" };

  const text = formatCardNumber(slot.value);
  return text === ABSENT_VALUE
    ? { state: "absent", text }
    : { state: "present", text };
}

function costCell(cost) {
  if (!cost) return { state: "not-applicable", text: "" };
  return cost.absent
    ? { state: "absent", text: ABSENT_VALUE }
    : { state: "present", text: formatCardNumber(cost.value) };
}

/* The site-net line under the Transfer total -- the one place the cards meet:
 *
 *     SITE NET COST                                  -13,715
 *     = utility bill -758 + generator fuel -13,623 + held in storage 666
 *
 * Each figure keeps the sign it has on its own card, so the fuel here can be
 * matched to the Production card's Fuel row by eye. The tooltip carries what
 * the line cannot: how closely it closes. See buildSiteReconciliation. */
const SITE_NET_NOTE = {
  closed: (residual) =>
    Math.abs(residual) < 0.005
      ? "Adds up exactly, from the engine's own columns."
      : `Adds up to within ${formatCardNumber(Math.abs(residual))}: the ` +
        "engine rounds each hourly cost to the cent.",
  unreconciled: () =>
    "These do not add up. The unreconciled term is the gap between the " +
    "engine's columns for this run.",
  incomplete: () =>
    "This run did not record every term (—), so the line cannot be checked.",
};

function SiteNetRows({ reconciliation, testIdPrefix }) {
  const { label, total, parts = [], residual, status } = reconciliation;
  const note = (SITE_NET_NOTE[status] || SITE_NET_NOTE.incomplete)(residual);

  return (
    <>
      <tr
        className="card-flow__row card-flow__row--site-net"
        data-row-variant="site-net"
        data-testid={`${testIdPrefix}-site-net`}
      >
        <th scope="row" colSpan={2} className="card-flow__site-net-label">
          {label}
        </th>
        <td
          colSpan={2}
          className="card-flow__site-net-value"
          title={note}
          data-testid={`${testIdPrefix}-site-net-value`}
        >
          {formatCardNumber(total)}
        </td>
      </tr>
      <tr
        className="card-flow__row card-flow__row--site-net-parts"
        data-row-variant="site-net-parts"
        data-reconciliation-status={status}
      >
        <td
          colSpan={4}
          className="card-flow__site-net-parts"
          title={note}
          data-testid={`${testIdPrefix}-site-net-parts`}
        >
          ={" "}
          {parts.map((part, index) => (
            <React.Fragment key={part.key}>
              {index > 0 ? " + " : null}
              <span
                className={`card-flow__site-net-part card-flow__site-net-part--${part.key}`}
                data-testid={`${testIdPrefix}-site-net-part-${part.key}`}
              >
                {part.label} {formatCardNumber(part.value)}
              </span>
            </React.Fragment>
          ))}
        </td>
      </tr>
    </>
  );
}

export default function CardFlowRows({
  rows = [],
  total,
  totalIncludesDemand = false,
  totalLabel = null,
  reconciliation = null,
  testIdPrefix,
  showFootnote = true,
  showColumnHeaders = false,
}) {
  const memo = showFootnote && hasMemoRow(rows);
  /* `undefined` means this card has no total row at all. `null` means it has
   * one and the figure is ABSENT -- which is a real state now that the total is
   * a net of two figures and either half can be missing. Collapsing the two
   * would silently drop the total row on a run whose demand attribution was
   * never recorded, which reads as "this card has no total" rather than "this
   * total could not be formed". */
  const hasTotal = total !== undefined;
  /* The total is a value cell like any other, so it carries the same state
   * marker and the same em-dash rather than a fabricated $0. */
  const totalText = hasTotal ? formatCardNumber(total) : "";
  const totalState = totalText === ABSENT_VALUE ? "absent" : "present";

  return (
    <div className="card-flow">
      <table className="card-flow__table">
        {/* One definition of the columns, shared by every row of every card.
          * With `table-layout: fixed` these percentages are the layout -- no
          * row can size a column to its own content. */}
        <colgroup>
          <col className="card-flow__col card-flow__col--label" />
          <col className="card-flow__col card-flow__col--power" />
          <col className="card-flow__col card-flow__col--energy" />
          <col className="card-flow__col card-flow__col--cost" />
        </colgroup>

        {/* A caption rather than a paragraph after the table: the note is about
          * the table, and `caption-side: bottom` puts it where it reads. */}
        {/* Two halves, and the second one is the point.
          *
          * "† Not included in the total" alone was enough when one row of three
          * carried a dagger. A storage card carries two, and a reader who sees
          * rows excluded will take the rest for the addends -- which, since the
          * Held and Fuel rows, is right: the rows without a dagger add up to
          * the total. The second clause says so rather than leaving it to be
          * inferred. It is a separate span so it can sit at a lighter weight
          * and wrap on its own line on a narrow card. */}
        {memo ? (
          <caption
            className="card-flow__footnote"
            data-testid={`${testIdPrefix}-memo-note`}
          >
            <span aria-hidden="true">†</span> {MEMO_CAPTION}
            <span className="card-flow__footnote-total"> — {TOTAL_CAPTION}</span>
          </caption>
        ) : null}

        {showColumnHeaders ? (
          <thead>
            <tr
              className="card-flow__row card-flow__row--head"
              data-row-variant="head"
              data-testid={`${testIdPrefix}-row-head`}
            >
              {/* The flow-name column. Blank on screen -- the row labels below
                * speak for themselves -- but named for screen readers. */}
              <th
                scope="col"
                className="card-flow__label card-flow__label--head"
                aria-label="Flow"
              />
              {COLUMN_HEADINGS.map(({ key, quantity, qualifier, unit, title }) => (
                <th
                  key={key}
                  scope="col"
                  className={`card-flow__head card-flow__head--${key}`}
                  title={title}
                  data-testid={`${testIdPrefix}-head-${key}`}
                >
                  {/* Both lines live inside this one <th>, so the heading stays
                    * a single column header for assistive technology. The line
                    * break is a block-level span rather than a <br>, so the two
                    * lines can be styled apart -- weight and size, not colour
                    * alone -- without becoming separate headings. */}
                  <span className="card-flow__head-quantity">{quantity}</span>
                  <span className="card-flow__head-detail">
                    {qualifier ? `${qualifier} ` : null}
                    {/* Its own span so the heading's uppercasing cannot turn
                      * "kWh" into "KWH". */}
                    <span className="card-flow__head-unit">({unit})</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
        ) : null}

        <tbody>
          {rows.map((row) => {
            if (!row) return null;

            /* A row hint explains the whole row (driving energy not recorded);
             * a cost hint explains just the money cell (column not persisted
             * yet). The money cell prefers its own. */
            const costHint = row.cost?.hint || row.hint;
            const power = statCell(row.power);
            const energy = statCell(row.energy);
            const cost = costCell(row.cost);

            return (
              <tr
                key={row.key}
                className={`card-flow__row card-flow__row--${row.variant}`}
                data-testid={`${testIdPrefix}-row-${row.key}`}
                data-row-variant={row.variant}
              >
                <th
                  scope="row"
                  className="card-flow__label"
                  title={row.hint}
                >
                  {row.label}
                  {/* The dagger's tooltip is the ROW's own reason, not one
                    * shared sentence. Driving and Losses are both outside the
                    * total but not for the same reason -- one is energy already
                    * paid for, the other money already counted -- and a
                    * sentence general enough to cover both would tell the
                    * reader they are the same kind of exclusion. */}
                  {row.variant === "memo" ? (
                    <span
                      className="card-flow__memo-mark"
                      aria-label="memo line, not included in the total"
                      title={row.memoNote || MEMO_FOOTNOTE}
                    >
                      †
                    </span>
                  ) : null}
                </th>

                <ValueCell
                  kind="power"
                  state={power.state}
                  text={power.text}
                  title={power.state === "absent" ? row.hint : undefined}
                  testId={`${testIdPrefix}-${row.key}-power`}
                />
                <ValueCell
                  kind="energy"
                  state={energy.state}
                  text={energy.text}
                  title={energy.state === "absent" ? row.hint : undefined}
                  testId={`${testIdPrefix}-${row.key}-energy`}
                />
                <ValueCell
                  kind="cost"
                  state={cost.state}
                  text={cost.text}
                  title={cost.state === "absent" ? costHint : undefined}
                  testId={`${testIdPrefix}-${row.key}-cost`}
                />
              </tr>
            );
          })}
        </tbody>

        {/* TOTAL lives in the table so it shares the columns, but its value
          * spans the last two so a seven-figure number is not squeezed into the
          * money column alone. It is read from the engine's own per-card total
          * and is never summed from the rows above it. */}
        {hasTotal ? (
          <tfoot>
            <tr
              className="card-flow__row card-flow__row--total"
              data-row-variant="total"
              data-testid={`${testIdPrefix}-total`}
            >
              {/* Labelled so the change is legible without a tooltip.
                *
                * Anyone who knew the old card read this number as the engine's
                * category cost. It is now that PLUS the demand charge, under
                * the same heading, which is exactly the kind of silent
                * redefinition that gets trusted wrongly. "NET TOTAL" with
                * "energy + demand" beneath it says what changed on the card
                * itself.
                *
                * The second line appears only when a demand charge was
                * actually folded in -- on an unfloored tariff with no demand
                * charge the total is the energy figure alone, and claiming
                * otherwise would be its own small lie. */}
              <th
                scope="row"
                colSpan={2}
                className="card-flow__total-label"
              >
                {/* A card may name its own total. Transfer does: its rows are
                  * what crossed the utility meter -- import, export, demand --
                  * so its total is the "Utility bill". A named label replaces
                  * both lines, because the name already carries what the
                  * sub-line existed to explain. */}
                {totalLabel ? (
                  <span className="card-flow__total-label-main">
                    {totalLabel}
                  </span>
                ) : (
                  <>
                    <span className="card-flow__total-label-main">
                      {totalIncludesDemand ? "Net Total" : "Total"}
                    </span>
                    {totalIncludesDemand ? (
                      <span className="card-flow__total-label-detail">
                        energy + demand
                      </span>
                    ) : null}
                  </>
                )}
              </th>
              <td
                colSpan={2}
                className="card-flow__total-value"
                data-cell-state={totalState}
                aria-label={CELL_STATE[totalState] || undefined}
                data-testid={`${testIdPrefix}-price`}
              >
                {totalText}
              </td>
            </tr>
            {reconciliation ? (
              <SiteNetRows
                reconciliation={reconciliation}
                testIdPrefix={testIdPrefix}
              />
            ) : null}
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
